// ===========================================================================
// ReHome — Távolságszámítás és távolság-alapú match pontozás
// ---------------------------------------------------------------------------
// A Smart Match eddig kizárólag személyiség/életmód alapján pontozott, a
// menhely helye egyáltalán nem számított — így egy 300 km-re lévő állat
// ugyanúgy "tökéletes pár" lehetett, mint a szomszéd utcában lévő.
//
// FONTOS TERVEZÉSI DÖNTÉS: a távolság MÓDOSÍTJA a pontszámot, de soha nem
// szűr ki senkit. Aki hajlandó messzebb menni egy hozzá illő állatért, azt
// nem szabad megfosztani a találattól — csak lejjebb kerül a listában.
// Ezért a hozzájárulás korlátos (l. DISTANCE_*_WEIGHT), és nem tudja
// felülírni az életmód-illeszkedést.
// ===========================================================================

/** Alapértelmezett vállalt távolság, ha a felhasználó nem állított be sajátot. */
export const DEFAULT_MAX_TRAVEL_KM = 50;

/** Felkínált távolság-opciók a profilban / Smart Matchben. */
export const TRAVEL_DISTANCE_OPTIONS = [
  { value: 25, label: '25 km — csak a környékemen' },
  { value: 50, label: '50 km — a megyémen belül' },
  { value: 100, label: '100 km — hajlandó vagyok utazni' },
  { value: 250, label: '250 km — az ország bármely részére' },
] as const;

/** Legfeljebb ennyi pont JÁR a nagyon közeli menhelyért. */
const DISTANCE_BONUS_WEIGHT = 12;
/** Legfeljebb ennyi pont VONÓDIK LE a vállalt távolságon túli menhelyért. */
const DISTANCE_PENALTY_WEIGHT = 18;

export interface Coordinates {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/**
 * Két koordináta közti nagykör-távolság kilométerben (haversine).
 *
 * Légvonalban mér, nem útvonalon — a pontozáshoz ez bőven elég, és nem
 * igényel külső útvonaltervező hívást minden állatra.
 */
export function haversineKm(a: Coordinates, b: Coordinates): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Érvényes koordinátapár-e (a `0,0` "Null-sziget" is hibás adatnak számít). */
export function isUsableCoordinate(lat: unknown, lng: unknown): boolean {
  return (
    typeof lat === 'number' && typeof lng === 'number' &&
    Number.isFinite(lat) && Number.isFinite(lng) &&
    !(lat === 0 && lng === 0) &&
    Math.abs(lat) <= 90 && Math.abs(lng) <= 180
  );
}

/**
 * A távolság hozzájárulása a match pontszámhoz.
 *
 * - a vállalt távolságon BELÜL: 0…+DISTANCE_BONUS_WEIGHT, lineárisan
 *   csökkenve (a helyben lévő menhely kapja a maximumot)
 * - a vállalt távolságon TÚL: negatív, de -DISTANCE_PENALTY_WEIGHT-nél
 *   sosem rosszabb — így a távoli állat lejjebb csúszik, de nem tűnik el
 *
 * @returns pontszám-módosító; 0, ha nincs mihez viszonyítani
 */
export function distanceMatchScore(distanceKm: number | null, maxTravelKm: number): number {
  if (distanceKm === null || !Number.isFinite(distanceKm)) return 0;
  const limit = maxTravelKm > 0 ? maxTravelKm : DEFAULT_MAX_TRAVEL_KM;

  if (distanceKm <= limit) {
    const closeness = 1 - distanceKm / limit; // 1 = helyben, 0 = épp a határon
    return Math.round(DISTANCE_BONUS_WEIGHT * closeness);
  }

  // A határon túl: a büntetés a túllépés arányában nő, de telítődik.
  const overshootRatio = (distanceKm - limit) / limit;
  const penalty = DISTANCE_PENALTY_WEIGHT * Math.min(1, overshootRatio);
  return -Math.round(penalty);
}

/** Ember által olvasható távolság ("12 km", "1,5 km", "230 km"). */
export function formatDistanceKm(distanceKm: number | null): string | null {
  if (distanceKm === null || !Number.isFinite(distanceKm)) return null;
  if (distanceKm < 10) return `${distanceKm.toFixed(1).replace('.', ',')} km`;
  return `${Math.round(distanceKm)} km`;
}
